import {
  mixColors,
  STORE_TEMPLATES,
  TEMPLATE_DEFAULTS,
  type StoreColors,
  type StoreTemplate,
} from '@avrahami/contracts';
import { he } from '../i18n/he';

/**
 * A small drawing of a template's home page (adr/0044), in the given colours or the template's
 * own: where the header, the headline, the departments and the pictures sit. Not the page
 * itself: the storefront draws that. Right to left, as the stores are.
 */
export function TemplatePreview({
  template,
  colors = TEMPLATE_DEFAULTS[template].colors,
}: {
  template: StoreTemplate;
  colors?: StoreColors;
}) {
  const picture = mixColors(colors.brand, '#ffffff', 0.72);
  const pictureDark = mixColors(colors.brand, '#ffffff', 0.5);
  const line = '#cbd5e1';
  return (
    <svg
      viewBox="0 0 160 100"
      role="img"
      aria-label={he.storeDesign.templates[template].name}
      className="h-auto w-full rounded-md bg-white ring-1 ring-slate-200"
    >
      {template === 'PANORAMA' ? (
        <>
          {/* A dark strip, the header and a row of the departments. */}
          <rect x="0" y="0" width="160" height="5" fill={colors.ink} />
          <rect x="0" y="5" width="160" height="16" fill="#ffffff" />
          <rect x="138" y="8" width="16" height="6" rx="1.5" fill={colors.brand} />
          <rect x="60" y="9" width="60" height="4" rx="2" fill="#eef1f4" />
          <rect x="146" y="16" width="8" height="3" rx="1" fill={colors.ink} />
          {[120, 104, 88, 72, 56].map((x) => (
            <rect key={x} x={x} y="16.5" width="12" height="2" fill={line} />
          ))}
          {/* The steel panel over the first screen: the headline and the departments' pictures. */}
          <rect x="0" y="21" width="160" height="56" fill="#eef1f4" />
          <rect x="100" y="32" width="54" height="7" fill={colors.ink} />
          <rect x="112" y="42" width="42" height="7" fill={colors.brand} />
          <rect x="128" y="56" width="26" height="7" rx="1.5" fill={colors.brand} />
          <rect x="100" y="56" width="24" height="7" rx="1.5" fill="#ffffff" />
          <rect x="10" y="28" width="38" height="22" rx="2" fill={pictureDark} />
          <rect x="52" y="30" width="30" height="18" rx="2" fill={picture} />
          <rect x="20" y="54" width="30" height="18" rx="2" fill={picture} />
          {/* The strip of departments along its bottom, the first one lit. */}
          {[128, 96, 64, 32, 0].map((x, index) => (
            <g key={x}>
              <rect
                x={x}
                y="77"
                width="32"
                height="13"
                fill={index === 0 ? colors.ink : '#ffffff'}
                stroke="#e2e8f0"
                strokeWidth="0.5"
              />
              <rect x={x + 4} y="81" width="20" height="2" fill={index === 0 ? '#ffffff' : line} />
              <rect x={x + 26} y="81" width="2" height="2" fill={colors.accent} />
            </g>
          ))}
        </>
      ) : null}
      {template === 'ATELIER' ? (
        <>
          {/* Warm paper, hairlines and a masthead with the name in the middle. */}
          <rect x="0" y="0" width="160" height="100" fill="#f3eee5" />
          <rect x="66" y="5" width="28" height="5" fill={colors.ink} />
          <rect x="0" y="14" width="160" height="0.6" fill={colors.ink} opacity="0.4" />
          <rect x="0" y="18" width="160" height="0.4" fill={colors.ink} opacity="0.25" />
          {/* The big headline, its second line in the brand colour, beside a numbered index. */}
          <rect x="92" y="26" width="62" height="9" fill={colors.ink} />
          <rect x="102" y="38" width="52" height="9" fill={colors.brand} />
          <rect x="124" y="54" width="30" height="6" fill={colors.ink} />
          {[26, 32, 38, 44, 50, 56].map((y) => (
            <g key={y}>
              <rect x="56" y={y} width="30" height="0.4" fill={colors.ink} opacity="0.35" />
              <rect x="72" y={y + 1.6} width="14" height="1.6" fill={colors.ink} opacity="0.7" />
            </g>
          ))}
          {/* A framed picture of the department. */}
          <rect
            x="6"
            y="24"
            width="44"
            height="40"
            fill="#e8e0d2"
            stroke={colors.ink}
            strokeWidth="0.6"
          />
          <rect x="12" y="32" width="32" height="22" fill={picture} />
          {/* The olive band of promises. */}
          <rect x="0" y="70" width="160" height="30" fill={colors.accent} />
          <rect x="110" y="76" width="44" height="6" fill="#f3eee5" />
          {[110, 60, 10].map((x) => (
            <rect key={x} x={x} y="88" width="40" height="0.5" fill="#f3eee5" opacity="0.5" />
          ))}
        </>
      ) : null}
      {template === 'DEPOT' ? (
        <>
          {/* Concrete, thick black frames and safety yellow. */}
          <rect x="0" y="0" width="160" height="100" fill="#eceae4" />
          <rect x="0" y="0" width="160" height="5" fill={colors.ink} />
          <rect x="120" y="0.8" width="36" height="3.4" fill={colors.accent} />
          <rect
            x="0"
            y="5"
            width="160"
            height="12"
            fill="#faf9f5"
            stroke={colors.ink}
            strokeWidth="1"
          />
          <rect x="140" y="7.5" width="14" height="7" fill={colors.accent} stroke={colors.ink} />
          <rect x="62" y="8" width="54" height="6" fill="#ffffff" stroke={colors.ink} />
          <rect x="62" y="8" width="12" height="6" fill={colors.accent} stroke={colors.ink} />
          {/* The headline in heavy letters, its second line on a yellow block. */}
          <rect x="88" y="24" width="66" height="10" fill={colors.ink} />
          <rect x="82" y="37" width="72" height="11" fill={colors.accent} />
          <rect x="94" y="51" width="60" height="7" fill="#ffffff" stroke={colors.ink} />
          <rect x="94" y="51" width="14" height="7" fill={colors.accent} stroke={colors.ink} />
          {/* A framed product with a hard shadow. */}
          <rect x="10" y="26" width="58" height="38" fill={colors.ink} />
          <rect
            x="6"
            y="22"
            width="58"
            height="38"
            fill="#ffffff"
            stroke={colors.ink}
            strokeWidth="1.2"
          />
          <rect x="14" y="28" width="42" height="22" fill={picture} />
          <rect x="6" y="52" width="10" height="8" fill={colors.accent} stroke={colors.ink} />
          {/* The aisle of departments, bin codes on top. */}
          {[132, 106, 80, 54, 28, 2].map((x, index) => (
            <g key={x}>
              <rect
                x={x}
                y="70"
                width="24"
                height="16"
                fill={index === 2 ? colors.accent : '#faf9f5'}
                stroke={colors.ink}
              />
              <rect x={x + 14} y="70" width="10" height="4" fill={colors.ink} />
              <rect x={x + 3} y="79" width="14" height="2" fill={colors.ink} />
            </g>
          ))}
          <rect x="0" y="90" width="160" height="10" fill={colors.ink} />
          <rect x="110" y="93" width="44" height="4" fill={colors.accent} />
          <rect x="54" y="93" width="44" height="4" fill={colors.accent} />
        </>
      ) : null}
      {template === 'FRESH' ? (
        <>
          {/* Mint ground, round cards and soft colours. */}
          <rect
            x="0"
            y="0"
            width="160"
            height="100"
            fill={mixColors(colors.brand, '#ffffff', 0.95)}
          />
          <rect x="6" y="4" width="148" height="14" rx="7" fill="#ffffff" />
          <rect x="138" y="7.5" width="12" height="7" rx="3.5" fill={colors.brand} />
          <rect x="62" y="8" width="54" height="6" rx="3" fill="#f1f5f2" />
          <rect x="10" y="7.5" width="18" height="7" rx="3.5" fill={colors.ink} />
          {/* The bento opening: the headline card, a picture on lilac, a dark and a coral card. */}
          <rect x="88" y="22" width="66" height="50" rx="8" fill="#ffffff" />
          <rect x="96" y="32" width="52" height="7" rx="2" fill={colors.ink} />
          <rect x="104" y="42" width="44" height="7" rx="2" fill={colors.brand} />
          <rect x="120" y="56" width="28" height="8" rx="4" fill={colors.brand} />
          <rect x="44" y="22" width="40" height="50" rx="8" fill="#ece6ff" />
          <circle cx="64" cy="44" r="13" fill="#d6cbff" />
          <rect x="52" y="38" width="24" height="14" rx="2" fill={pictureDark} />
          <rect x="6" y="22" width="34" height="23" rx="8" fill={colors.ink} />
          <rect x="12" y="29" width="16" height="5" rx="1" fill="#ffffff" />
          <rect x="6" y="49" width="34" height="23" rx="8" fill={colors.accent} />
          <rect x="12" y="56" width="20" height="3" rx="1" fill="#ffffff" />
          {/* The departments as round chips. */}
          <rect x="6" y="77" width="148" height="16" rx="8" fill="#ffffff" />
          {[134, 110, 86, 62, 38, 14].map((x, index) => (
            <rect
              key={x}
              x={x}
              y="81"
              width="20"
              height="8"
              rx="4"
              fill={index === 0 ? colors.brand : '#eef3ec'}
            />
          ))}
        </>
      ) : null}
      {template === 'PLANNER' ? (
        <>
          {/* White and warm grey, the brand colour on every action. */}
          <rect x="0" y="0" width="160" height="100" fill="#ffffff" />
          <rect x="0" y="0" width="160" height="4" fill="#f5f4f2" />
          <rect
            x="0"
            y="4"
            width="160"
            height="14"
            fill="#ffffff"
            stroke="#e7e4e0"
            strokeWidth="0.5"
          />
          <rect x="142" y="7" width="12" height="8" rx="2" fill={colors.brand} />
          <rect x="62" y="8" width="54" height="6" rx="3" fill="#f5f4f2" />
          <rect x="6" y="8" width="20" height="6" rx="1.5" fill={colors.brand} />
          <rect
            x="0"
            y="18"
            width="160"
            height="48"
            fill={mixColors(colors.brand, '#ffffff', 0.95)}
          />
          {/* The headline, and the "what are you equipping?" chooser beside it. */}
          <rect x="100" y="26" width="54" height="7" fill={colors.ink} />
          <rect x="108" y="36" width="46" height="7" fill={colors.brand} />
          <rect x="126" y="50" width="28" height="8" rx="2" fill={colors.brand} />
          <rect x="96" y="50" width="26" height="8" rx="2" fill="#ffffff" stroke="#e7e4e0" />
          <rect x="6" y="22" width="80" height="40" rx="4" fill="#ffffff" stroke="#e7e4e0" />
          {[10, 48].map((x) =>
            [28, 39, 50].map((y) => (
              <g key={`${String(x)}-${String(y)}`}>
                <rect x={x} y={y} width="34" height="9" rx="2" fill="#ffffff" stroke="#e7e4e0" />
                <circle
                  cx={x + 29}
                  cy={y + 4.5}
                  r="2.6"
                  fill={mixColors(colors.brand, '#ffffff', 0.85)}
                />
              </g>
            )),
          )}
          {/* The kitchen's flow: numbered stages along a dashed line. */}
          <rect x="6" y="73" width="148" height="0.6" fill={colors.brand} opacity="0.4" />
          {[142, 112, 82, 52, 22].map((x) => (
            <g key={x}>
              <circle cx={x} cy="73.3" r="3.4" fill={colors.brand} />
              <rect x={x - 12} y="80" width="24" height="14" rx="3" fill="#f5f4f2" />
              <circle cx={x + 8} cy="83" r="1.6" fill={colors.accent} />
            </g>
          ))}
        </>
      ) : null}
    </svg>
  );
}

/**
 * The templates as large cards to choose from, each with its drawing, name and description. A
 * radio group: one is always chosen. The chosen card is drawn in `colors` (the form's own); the
 * others in their own colours.
 */
export function TemplatePicker({
  value,
  onChange,
  colors,
  disabled = false,
  name = 'template',
}: {
  value: StoreTemplate;
  onChange: (template: StoreTemplate) => void;
  colors?: StoreColors;
  disabled?: boolean;
  name?: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={he.storeDesign.template}
      className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5"
      data-testid="template-picker"
    >
      {STORE_TEMPLATES.map((template, index) => {
        const chosen = value === template;
        const words = he.storeDesign.templates[template];
        return (
          <label
            key={template}
            data-testid={`template-${template}`}
            data-chosen={chosen}
            className={`flex cursor-pointer flex-col gap-2 rounded-lg p-3 ring-2 transition focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-brand-700 ${
              chosen ? 'bg-brand-50 ring-brand-600' : 'bg-white ring-slate-200 hover:ring-slate-300'
            } ${disabled ? 'cursor-not-allowed opacity-70' : ''}`}
          >
            <input
              type="radio"
              name={name}
              value={template}
              checked={chosen}
              disabled={disabled}
              className="sr-only"
              onChange={() => {
                onChange(template);
              }}
            />
            <TemplatePreview
              template={template}
              {...(chosen && colors !== undefined ? { colors } : {})}
            />
            <span className="font-semibold text-slate-900">
              <span className="text-slate-500">{index + 1}. </span>
              {words.name}
            </span>
            <span className="text-sm text-slate-600">{words.description}</span>
          </label>
        );
      })}
    </div>
  );
}
